import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import { format, parse } from "date-fns";
import CI from "@trackcode/ci";
import { Icon } from "@trackcode/ui";

const PANEL_MIN_WIDTH = 280;
const PANEL_MAX_WIDTH = 600;
const VIEWPORT_PADDING = 8;

const FilterContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${CI.color.gray};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: ${CI.color.darkGray};

  &:hover {
    border-color: ${CI.color.primary};
  }
`;

const DropdownPanel = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${CI.color.gray};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  display: flex;
  width: ${props => props.$width}px;
  max-width: calc(100vw - ${VIEWPORT_PADDING * 2}px);
`;

const PredefinedRanges = styled.div`
  padding: 16px;
  border-right: 1px solid ${CI.color.lightGray};
  min-width: 180px;
  flex-shrink: 0;
`;

const RangeOption = styled.button`
  display: block;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: ${CI.color.darkGray};
  border-radius: 4px;
  margin-bottom: 2px;

  &:hover {
    background: ${CI.color.lightGray};
  }
`;

const CalendarSection = styled.div`
  padding: 16px;
  flex: 1;
  min-width: 0;
`;

const DateInputs = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${CI.color.gray};
  border-radius: 4px;
  font-size: 14px;
  color: ${CI.color.darkGray};

  &:focus {
    outline: none;
    border-color: ${CI.color.primary};
  }
`;

const ApplyButton = styled.button`
  background: ${CI.color.primary};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: ${CI.color.primaryDark};
  }
`;

const computePanelPosition = (buttonEl, panelAlign) => {
  const rect = buttonEl.getBoundingClientRect();
  const panelWidth = Math.min(
    PANEL_MAX_WIDTH,
    Math.max(PANEL_MIN_WIDTH, window.innerWidth - VIEWPORT_PADDING * 2)
  );

  let left =
    panelAlign === "end" ? rect.right - panelWidth : rect.left;
  left = Math.max(
    VIEWPORT_PADDING,
    Math.min(left, window.innerWidth - panelWidth - VIEWPORT_PADDING)
  );

  return {
    top: rect.bottom + 4,
    left,
    width: panelWidth
  };
};

const DateRangeFilter = ({
  startDate,
  endDate,
  onDateChange,
  panelAlign = "end"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [panelPosition, setPanelPosition] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setPanelPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!buttonRef.current) return;
      setPanelPosition(computePanelPosition(buttonRef.current, panelAlign));
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, panelAlign]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = event => {
      const target = event.target;
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const startOfDay = date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const startOfCalendarWeek = date => {
    const d = startOfDay(date);
    const day = d.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diffToMonday);
    return d;
  };

  const predefinedRanges = [
    {
      key: "today",
      label: "Heute",
      getDates: () => {
        const today = startOfDay(new Date());
        const tomorrow = addDays(today, 1);
        return { start: today, end: tomorrow };
      }
    },
    {
      key: "yesterday",
      label: "Gestern",
      getDates: () => {
        const today = startOfDay(new Date());
        const yesterday = addDays(today, -1);
        return { start: yesterday, end: today };
      }
    },
    {
      key: "thisWeek",
      label: "Diese Woche",
      getDates: () => {
        const today = new Date();
        const start = startOfCalendarWeek(today);
        const end = addDays(start, 7);
        return { start, end };
      }
    },
    {
      key: "lastWeek",
      label: "Letzte Woche",
      getDates: () => {
        const today = new Date();
        const thisWeekStart = startOfCalendarWeek(today);
        const start = addDays(thisWeekStart, -7);
        const end = new Date(thisWeekStart);
        return { start, end };
      }
    },
    {
      key: "thisMonth",
      label: "Diesen Monat",
      getDates: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        return { start, end };
      }
    },
    {
      key: "lastMonth",
      label: "Letzten Monat",
      getDates: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start, end };
      }
    },
    {
      key: "thisYear",
      label: "Dieses Jahr",
      getDates: () => {
        const today = new Date();
        const start = new Date(today.getFullYear(), 0, 1);
        const end = new Date(today.getFullYear() + 1, 0, 1);
        return { start, end };
      }
    }
  ];

  const formatDateRange = (start, end) => {
    if (!start || !end) return "Zeitraum auswählen";
    return `${start.toLocaleDateString("de-DE")} - ${end.toLocaleDateString("de-DE")}`;
  };

  const handlePredefinedRange = range => {
    const { start, end } = range.getDates();
    setTempStartDate(start);
    setTempEndDate(end);
    onDateChange(start, end, range.key);
    setIsOpen(false);
  };

  const handleApply = () => {
    onDateChange(tempStartDate, tempEndDate, "custom");
    setIsOpen(false);
  };

  const formatDateForInput = date => {
    if (!date) return "";
    return format(date, "YYYY-MM-DD");
  };

  const parseDateInput = value => {
    if (!value) return null;
    return startOfDay(parse(value, "YYYY-MM-DD", new Date()));
  };

  const handleStartDateChange = e => {
    const date = parseDateInput(e.target.value);
    if (date) setTempStartDate(date);
  };

  const handleEndDateChange = e => {
    const date = parseDateInput(e.target.value);
    if (date) setTempEndDate(date);
  };

  const panel =
    isOpen && panelPosition
      ? createPortal(
          <DropdownPanel
            ref={panelRef}
            $width={panelPosition.width}
            style={{
              top: panelPosition.top,
              left: panelPosition.left
            }}
            role="dialog"
            aria-label="Zeitraum auswählen"
          >
            <PredefinedRanges>
              <h4
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  color: CI.color.darkGray
                }}
              >
                Schnellauswahl
              </h4>
              {predefinedRanges.map(range => (
                <RangeOption
                  key={range.key}
                  type="button"
                  onClick={() => handlePredefinedRange(range)}
                >
                  {range.label}
                </RangeOption>
              ))}
            </PredefinedRanges>

            <CalendarSection>
              <h4
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  color: CI.color.darkGray
                }}
              >
                Datum auswählen
              </h4>

              <DateInputs>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: CI.color.mediumGray,
                      marginBottom: "4px"
                    }}
                  >
                    Von:
                  </label>
                  <DateInput
                    type="date"
                    value={formatDateForInput(tempStartDate)}
                    onChange={handleStartDateChange}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: CI.color.mediumGray,
                      marginBottom: "4px"
                    }}
                  >
                    Bis:
                  </label>
                  <DateInput
                    type="date"
                    value={formatDateForInput(tempEndDate)}
                    onChange={handleEndDateChange}
                  />
                </div>
              </DateInputs>

              <ApplyButton type="button" onClick={handleApply}>
                Filter anwenden
              </ApplyButton>
            </CalendarSection>
          </DropdownPanel>,
          document.body
        )
      : null;

  return (
    <FilterContainer>
      <FilterButton
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Icon name="calendar" />
        {formatDateRange(startDate, endDate)}
        <Icon name="chevron-down" />
      </FilterButton>
      {panel}
    </FilterContainer>
  );
};

DateRangeFilter.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onDateChange: PropTypes.func.isRequired,
  /** Align panel to start (left) or end (right) of trigger button. */
  panelAlign: PropTypes.oneOf(["start", "end"])
};

export default DateRangeFilter;
